import {AxiosResponse} from 'axios';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';
import {
  EntityOccurrence,
  EntityOccurrenceType,
  EntityOccurrenceTypeCategory,
  EntityOccurrenceTypeUpdateResponse,
} from '../backend/responses/entity-occurrence';
import {Attachment} from '../backend/interfaces/attachment';

export class EntityOccurrencesRepository {
  // Singleton Instance
  private static _instance: EntityOccurrencesRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityOccurrencesRepository {
    if (!this._instance) {
      this._instance = new EntityOccurrencesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityOccurrencesRepository');
  }

  public async getOccurrencesTypes(entityId: string): Promise<EntityOccurrenceType[]> {
    let url: string = `/entities/${entityId}/occurrences-types?includeComplementaryInformation=true&localize=true`;

    const result: AxiosResponse<EntityOccurrenceType[]> =
      await BackendApiManager.instance.get<EntityOccurrenceType[]>(url);

    return result.data;
  }

  public async addOccurrenceType(
    entityId: string,
    data: {
      name: string;
      category: EntityOccurrenceTypeCategory;
      isActive: boolean;
      actionRequired: boolean;
      notifyRolesIds?: string[];
    }
  ): Promise<EntityOccurrenceTypeUpdateResponse> {
    let url: string = `/entities/${entityId}/occurrences-types`;

    const answer: AxiosResponse<EntityOccurrenceTypeUpdateResponse> =
      await BackendApiManager.instance.post(url, data);

    return answer.data;
  }

  public async updateOccurrenceType(
    entityId: string,
    id: string,
    data: {
      name: string;
      category: EntityOccurrenceTypeCategory;
      isActive: boolean;
      actionRequired: boolean;
      notifyRolesIds?: string[];
    }
  ) {
    let url: string = `/entities/${entityId}/occurrences-types/${id}`;

    const answer: AxiosResponse<EntityOccurrenceTypeUpdateResponse> =
      await BackendApiManager.instance.put(url, data);

    return answer.data;
  }

  public async deleteOccurrenceType(entityId: string, id: string): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences-types/${id}`;

    await BackendApiManager.instance.delete(url);
  }

  public async getOccurrences(
    entityId: string,
    startDate?: string,
    endDate?: string
  ): Promise<EntityOccurrence[]> {
    let url: string = `/entities/${entityId}/occurrences`;

    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`;
    }

    const result: AxiosResponse<EntityOccurrence[]> =
      await BackendApiManager.instance.get<EntityOccurrence[]>(url);

    return result.data;
  }

  public async addOccurrence(
    entityId: string,
    data: {
      typeId: string;
      registeredAt: Date;
      targetUserId?: string;
      responsibleUserId?: string;
      details: string;
      siteName?: string | null;
      attachments?: Attachment[];
    }
  ): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences`;

    await BackendApiManager.instance.post(url, data);
  }

  public async acknowledgeOccurrence(
    entityId: string,
    occurrenceId: string,
    notes?: string
  ): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences/${occurrenceId}/acknowledge`;

    await BackendApiManager.instance.patch(url, {notes});
  }

  public async handleOccurrence(
    entityId: string,
    occurrenceId: string,
    handled: boolean,
    notes?: string
  ): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences/${occurrenceId}/handle`;

    await BackendApiManager.instance.put(url, {
      markedAsHandled: handled,
      notes,
    });
  }

  public async annotateOccurrence(
    entityId: string,
    occurrenceId: string,
    notes: string
  ): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences/${occurrenceId}/annotate`;

    await BackendApiManager.instance.post(url, {notes});
  }

  public async getOccurrencesCount(entityId: string): Promise<number> {
    let url: string = `/entities/${entityId}/occurrences/count?acknowledged=false`;

    const result: AxiosResponse<number> = await BackendApiManager.instance.get<number>(url);

    return result.headers['x-total-count'];
  }

  public async editOccurrence(
    entityId: string,
    occurrenceId: string,
    typeId: string,
    registeredAt: Date,
    details: string,
    attachments: Attachment[]
  ): Promise<void> {
    let url: string = `/entities/${entityId}/occurrences/${occurrenceId}`;

    await BackendApiManager.instance.put(url, {typeId, registeredAt, details, attachments});
  }
}
